class GiftWrapPanel extends HTMLElement {
  connectedCallback() {
    this._saveTimer = null;
    this._saving = false;

    this.querySelectorAll('.gift-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => this.save(), 200);
      });
    });

    if (!this._reconciled) {
      this._reconciled = true;
      this._reconcile();
    }
  }

  _setLoading(active) {
    this._saving = active;
    this.querySelectorAll('.gift-checkbox').forEach((cb) => {
      cb.disabled = active;
    });
  }

  async _resolveGiftVariantId() {
    if (window._giftWrapVariantId) {
      return window._giftWrapVariantId;
    }
    if (this.dataset.giftVariantId) {
      window._giftWrapVariantId = String(this.dataset.giftVariantId);
      return window._giftWrapVariantId;
    }

    // Fallback 1: confezione-regalo
    try {
      const res = await fetch('/products/confezione-regalo.js', {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.variants?.[0]?.id) {
          window._giftWrapVariantId = String(data.variants[0].id);
          return window._giftWrapVariantId;
        }
      }
    } catch (_) {}

    // Fallback 2: gift-box
    try {
      const res = await fetch('/products/gift-box.js', {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.variants?.[0]?.id) {
          window._giftWrapVariantId = String(data.variants[0].id);
          return window._giftWrapVariantId;
        }
      }
    } catch (_) {}

    return null;
  }

  async _reconcile() {
    try {
      const giftVariantId = await this._resolveGiftVariantId();
      if (!giftVariantId) return;

      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      let expectedGiftCount = 0;
      let actualGiftItem = null;

      for (const item of cart.items) {
        if (
          String(item.variant_id) === String(giftVariantId) ||
          item.handle === 'confezione-regalo' ||
          item.handle === 'gift-box'
        ) {
          actualGiftItem = item;
        } else if (item.properties?._gift_wrap === 'true') {
          expectedGiftCount += 1;
        }
      }

      const actualCount = actualGiftItem ? actualGiftItem.quantity : 0;
      if (expectedGiftCount !== actualCount) {
        if (expectedGiftCount > 0) {
          if (actualGiftItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: actualGiftItem.key, quantity: expectedGiftCount }),
            });
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: Number(giftVariantId), quantity: expectedGiftCount }),
            });
          }
        } else if (actualGiftItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: actualGiftItem.key, quantity: 0 }),
          });
        }
      }
    } catch (e) {
      console.warn('[GiftWrap] reconcile error:', e);
    }
  }

  async save() {
    if (this._saving) return;
    this._setLoading(true);

    try {
      const giftVariantId = await this._resolveGiftVariantId();
      const warningEl = this.querySelector('.ms-gift-warning');

      if (!giftVariantId) {
        if (warningEl) {
          warningEl.classList.remove('hidden');
        }
        console.warn(
          '[GiftWrap] Prodotto "confezione-regalo" non trovato in Shopify Admin. ' +
          'Crea il prodotto "Confezione Regalo" (handle: confezione-regalo) a €2,50 per addebitare il costo.'
        );
      } else if (warningEl) {
        warningEl.classList.add('hidden');
      }

      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const checkboxes = [...this.querySelectorAll('.gift-checkbox')];
      let checkedCount = 0;

      // STEP 1: Update line item properties
      for (const checkbox of checkboxes) {
        const variantId = String(checkbox.dataset.variantId);
        const cartItem = cart.items.find(
          (i) =>
            String(i.variant_id) === variantId &&
            String(i.variant_id) !== String(giftVariantId || '')
        );
        if (!cartItem) continue;

        if (checkbox.checked) {
          checkedCount++;
        }

        const isCurrentlyWrapped = cartItem.properties?._gift_wrap === 'true';
        if (checkbox.checked !== isCurrentlyWrapped) {
          const properties = { ...(cartItem.properties || {}) };
          properties['_gift_wrap'] = checkbox.checked ? 'true' : '';

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: cartItem.key, quantity: cartItem.quantity, properties }),
          });
        }
      }

      // STEP 2: Sync gift wrap product quantity in cart
      if (giftVariantId) {
        const freshCart = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        const giftItem = freshCart.items.find(
          (i) =>
            String(i.variant_id) === String(giftVariantId) ||
            i.handle === 'confezione-regalo' ||
            i.handle === 'gift-box'
        );

        if (checkedCount > 0) {
          if (giftItem) {
            if (giftItem.quantity !== checkedCount) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: giftItem.key, quantity: checkedCount }),
              });
            }
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: Number(giftVariantId), quantity: checkedCount }),
            });
          }
        } else if (giftItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
          });
        }
      }

      // STEP 3: Refresh UI
      const drawer = document.querySelector('cart-drawer');
      if (drawer && (drawer.classList.contains('active') || drawer.classList.contains('animate'))) {
        try {
          const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
          const data = await res.json();
          if (data['cart-drawer']) {
            const dom = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
            const newInner = dom.querySelector('.drawer__inner');
            const currentInner = drawer.querySelector('.drawer__inner');
            if (newInner && currentInner) {
              currentInner.innerHTML = newInner.innerHTML;
            }
          }
          if (data['cart-icon-bubble']) {
            const target = document.getElementById('cart-icon-bubble');
            if (target) {
              const dom = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
              const newBubble = dom.querySelector('.shopify-section') || dom.body.firstElementChild;
              if (newBubble) target.innerHTML = newBubble.innerHTML;
            }
          }
        } catch (err) {
          console.error('[GiftWrap] drawer refresh failed:', err);
          window.location.reload();
        }
        this._setLoading(false);
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('[GiftWrap] save failed:', err);
      this._setLoading(false);
    }
  }
}

if (!customElements.get('gift-wrap-panel')) {
  customElements.define('gift-wrap-panel', GiftWrapPanel);
}
